import { Inject, Injectable } from "@angular/core";

import {
    AuthenticationResult,
    InteractionRequiredAuthError,
} from "@azure/msal-browser";
import { MsalService } from "@azure/msal-angular";
import { Client, PageCollection } from "@microsoft/microsoft-graph-client";
import { getCurrentUserFromStorage, setCurrentUserInStorage } from "@app/utils/storage-utils";
import { protectedResources } from "../auth-config";
import { AzureUser } from "@app-models/azure-user";
import { GlobalConstants } from "@app-utils/global-constants";
import { AlertService } from "@app/modules/alert";
import { groups } from "@app/auth-config";
import { WorkRequest } from "@app/models/work-request";
import { emailAddress, emailMessage } from "@app/models/graph";
import { environment } from "environments/environment";
import { APP_BASE_HREF } from "@angular/common";
import { ApplicationInsightsLoggingService } from "./application-insights-logging.service";



@Injectable({
    providedIn: "root",
})
export class GraphService {

    env = environment;

    loginFlag = false;
    currentUser: AzureUser;
    errorOptions = {
        autoClose: false,
        keepAfterRouteChange: false,
    };
    constructor(
        private authService: MsalService,
        private alertService: AlertService,
        private applicationInsightsLoggingService: ApplicationInsightsLoggingService,
        @Inject(APP_BASE_HREF) private baseHref:string
    ) {
       
    }

    private getGraphClient(accessToken: string) {
        // Initialize Graph client
        const graphClient = Client.init({
            // Use the provided access token to authenticate requests
            authProvider: (done) => {
                done(null, accessToken);
            },
        });

        return graphClient;
    }

    private async getToken(scopes: string[]): Promise<string> {
        let authResponse: AuthenticationResult | null = null;
        await this.authService.instance.initialize();

        try {
            authResponse = await this.authService.instance.acquireTokenSilent({
                account: this.authService.instance.getActiveAccount()!,
                scopes: scopes,
            });
        } catch (error) {
            if (error instanceof InteractionRequiredAuthError) {
                // get default interaction type from auth config
                authResponse =
                    await this.authService.instance.acquireTokenPopup({
                        scopes: protectedResources.apiGraph.scopes,
                    });
            }
            console.log(error);
        }
        return authResponse ? authResponse.accessToken : "";
    }

    //Status updates go to the submittor and enterprise IT. it goes to the PM(s) if it a project.
    async sendStatusEmail(workRequest: WorkRequest = []) {
        let currentUser = getCurrentUserFromStorage();
        let subject = "Enterprise Technology Hub" + (environment.envLabel !== '' ? " (" + environment.envLabel + ")": "") + ": Status of " + workRequest.wrNumber + " changed to " + workRequest.status;
        let content = this.addAppLogo();

        content += "The status of Supply Ontario IT Work Request " + workRequest.wrNumber + " - " + workRequest.subject +
            "<br/> has been updated to: " + workRequest.status + "." + 
            "<br/><br>Request Initiator: " + workRequest.requestorName;

        if (workRequest.resolution && workRequest.status === GlobalConstants.STATUS_CLOSED) {
            content+=  "<br/><br>Resolution: " + workRequest.resolution;
        } 

        const toRecipients: emailAddress[] = [];
        const ccRecipients: emailAddress[] = [];

        if (environment.notifyEnterpriseIT) {
            toRecipients.push({ 'emailAddress': { address: environment.appEmailAddress } });
        }

        if (workRequest.requestResources && workRequest.requestResources.length > 0) {
            for (const rr of workRequest.requestResources) { 
                if (rr.role === GlobalConstants.PROJECT_MANAGER_ROLE) {
                    if (rr.email){
                        this.addRecipient(toRecipients, ccRecipients, rr.email);
                        //ccRecipients.push({ 'emailAddress': { address: rr.email! } });
                    }
                    content += "<br/>Project Manager: " + rr.name;
                }
            }
        }

        content += "<br><br><a href='" + location.origin + this.baseHref + "work-request/" +  workRequest.workRequestId + "'>Enterprise Technology Hub: " + workRequest.wrNumber + "</a>";
        if (environment.envLabel != '') {
            content += "<br/><br/><i>This email has been sent from a NON-PRODUCTION system (" + environment.envLabel + ") as part of testing.</i>";
        }

        //This should always exist, but in case there is an issue
        if ( workRequest.requestorEmail ) {
            if (toRecipients.length > 0) {
                this.addRecipient(toRecipients, ccRecipients, workRequest.requestorEmail);
                //ccRecipients.push({ 'emailAddress': { address: workRequest.requestorEmail } });
            } else {
                toRecipients.push({ 'emailAddress': { address: workRequest.requestorEmail } });
            }
        }

        const email: emailMessage = {

            message: {
                subject: subject,
                body: {
                    contentType: "HTML",
                    content: content
                },
                toRecipients: toRecipients,
                ccRecipients: ccRecipients,
                hasAttachments : true,
                    attachments : [
                    {
                            "@odata.type" : "#microsoft.graph.fileAttachment",
                            contentBytes : environment.ITHubImageBase64,
                            contentType : "image/png",
                            contentId : "applogo",
                            name : "applogo.png"
                        }
                    ]
            },
            saveToSentItems: true
        }

        try {
            const accessToken = await this.getToken(
                protectedResources.apiGraph.scopes
            );

            // Get a graph client instance for the given access token
            const graphClient = this.getGraphClient(accessToken);

            if (toRecipients.length > 0) {
                const response: PageCollection = await graphClient
                    .api(
                        protectedResources.apiGraph.endpoint + "/me/sendMail"
                    )
                    .post(email);
            }
            this.applicationInsightsLoggingService.logTrace("Work Request (" + workRequest.workRequestId + ") status changed to: " + workRequest.status + " by: " + currentUser.displayName);
            this.applicationInsightsLoggingService.logEvent("Work Request Status Changed to: " + workRequest.status );

            if ( workRequest.status === GlobalConstants.STATUS_CLOSED && 
                 GlobalConstants.FEEDBACK_REQUESTS.find(i => i === workRequest.requestType )) {
                this.sendFeedbackEmail(workRequest);
            }

        } catch (error: any) {
            console.log(error);
            this.alertService.error(
                "Error sending status notification email to initiator of request: " + error.statusCode + " - " + error.message,
                this.errorOptions
            );
        }


    }

        async sendFeedbackEmail(workRequest: WorkRequest = []) {
            let subject = "Enterprise Technology Hub" + (environment.envLabel !== '' ? " (" + environment.envLabel + ")": "") + ": Please provide feedback for request: " + workRequest.wrNumber + ".";
            let content = this.addAppLogo();



            content += "The Supply Ontario IT Work Request " + workRequest.wrNumber + " - " + workRequest.subject + " <br/>has been closed."; 
         
            if (workRequest.resolution ) {
                content+=  "<br/><br/>Resolution: " + workRequest.resolution;
            } 
            content += "<br/><br/>We would appreciate your feedback by completing this short form: "  +
                "<a href='" +  location.origin + this.baseHref + "feedback/" +  workRequest.workRequestId  + "'>Supply Ontario Enterprise Technology Feedback form</a>"; 
    
            if (environment.envLabel != '') {
                content += "<br/><br/><i>This email has been sent from a NON-PRODUCTION system (" + environment.envLabel + ") as part of testing.</i>";
            }
            const toRecipients: emailAddress[] = [];

            //This should always exist, but in case there is an issue
            if ( workRequest.requestorEmail ) {
                toRecipients.push({ 'emailAddress': { address: workRequest.requestorEmail } });
            }
    
            const email: emailMessage = {
    
                message: {
                    subject: subject,
                    body: {
                        contentType: "HTML",
                        content: content
                    },
                    toRecipients: toRecipients,
                    hasAttachments : true,
                    attachments : [
                    {
                            "@odata.type" : "#microsoft.graph.fileAttachment",
                            contentBytes : environment.ITHubImageBase64,
                            contentType : "image/png",
                            contentId : "applogo",
                            name : "applogo.png"
                        }
                    ]
                },
                saveToSentItems: true
            }
    
            try {
                const accessToken = await this.getToken(
                    protectedResources.apiGraph.scopes
                );
    
                // Get a graph client instance for the given access token
                const graphClient = this.getGraphClient(accessToken);
    
                if (toRecipients.length > 0) {
                    const response: PageCollection = await graphClient
                        .api(
                            protectedResources.apiGraph.endpoint + "/me/sendMail"
                        )
                        .post(email);
                }
            } catch (error: any) {
                console.log(error);
                this.alertService.error(
                    "Error sending status notification email to initiator of request: " + error.statusCode + " - " + error.message,
                    this.errorOptions
 
               );
 
            }
    
    
        }

    //We email submitted (for reference), enterpriseIT and the sponsor
    async sendNewRequestEmail(workRequest: WorkRequest = []) {
        if (!environment.sendNotificationOnSubmit) {
            return;
        }
        const currentUser = getCurrentUserFromStorage();
        let subject = "Enterprise Technology Hub" + (environment.envLabel !== '' ? " (" + environment.envLabel + ")": "") + 
        ": A new " + workRequest.requestType + 
        (workRequest.consultationType ? "(" + workRequest.consultationType + ")" : " ")  + 
        " request (" + workRequest.wrNumber + ") was submitted.";
       
        let content = this.addAppLogo();
        content += "A new " + workRequest.requestType + (workRequest.consultationType ? "(" + workRequest.consultationType + ")" : " ") + " request: " + workRequest.wrNumber + " - " + workRequest.subject +
            "<br/>was submitted." +
            "<br/><br/>Business requirements: " + workRequest.businessRequirements + 
            "<br>Request Initiator: " + workRequest.requestorName;


        const toRecipients: emailAddress[] = [];
        const ccRecipients: emailAddress[] = [];

        //SML TODO
        // If consultationType = security) add 'secruity email' to TO
        if (environment.notifyEnterpriseIT) {
            toRecipients.push({ 'emailAddress': { address: environment.appEmailAddress } });
        }
        if (workRequest.sponsors && workRequest.sponsors.length > 0) {
            if (workRequest.sponsors[0].email) {
                this.addRecipient(toRecipients, ccRecipients, workRequest.sponsors[0].email);
                //ccRecipients.push({ 'emailAddress': { address: workRequest.sponsors[0].email! } });
            }
            content += "<br/>Sponsor&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;: " + workRequest.sponsors[0].name;

        }
        if (workRequest.requestorEmail) {
            if (toRecipients.length > 0) {
                this.addRecipient(toRecipients, ccRecipients, workRequest.requestorEmail);
                //ccRecipients.push({ 'emailAddress': { address: workRequest.requestorEmail } });
            } else {
                toRecipients.push({ 'emailAddress': { address: workRequest.requestorEmail } });
            }
        }
        if ( workRequest.requestNotes!= null) {
            content += "<br/><br>Notes: " +  workRequest.requestNotes;
        }
        content += "<br><br><a href='" + location.origin + this.baseHref + "work-request/" +  workRequest.workRequestId + "'>Enterprise Technology Hub: " + workRequest.wrNumber + "</a>";
        content += "<br><br>" + GlobalConstants.RETURN_RESPONSE_MSG;

        if (environment.envLabel != '') {
            content += "<br/><br/><i>This email has been sent from a NON-PRODUCTION system (" + environment.envLabel + ") as part of testing.</i>";
        }
        const email: emailMessage = {

            message: {
                subject: subject,
                body: {
                    contentType: "HTML",
                    content: content
                },
                toRecipients: toRecipients,
                ccRecipients: ccRecipients,
                hasAttachments : true,
                    attachments : [
                    {
                            "@odata.type" : "#microsoft.graph.fileAttachment",
                            contentBytes : environment.ITHubImageBase64,
                            contentType : "image/png",
                            contentId : "applogo",
                            name : "applogo.png"
                        }
                    ]
            },
            saveToSentItems: true
        }

        try {
            const accessToken = await this.getToken(
                protectedResources.apiGraph.scopes
            );

            // Get a graph client instance for the given access token
            const graphClient = this.getGraphClient(accessToken);

            if (toRecipients.length > 0) {
                const response: PageCollection = await graphClient
                    .api(
                        protectedResources.apiGraph.endpoint + "/me/sendMail"
                    )
                    .post(email);
            }
            this.applicationInsightsLoggingService.logTrace("Work Request (" + workRequest.workRequestId + ") submitted by: " +  currentUser.displayName);
            this.applicationInsightsLoggingService.logEvent("Work Request Submitted");

        } catch (error: any) {
            console.log(error);
            this.alertService.error(
                "Error sending notification email to " + environment.appEmailAddress + ": " + error.statusCode + " - " + error.message,
                this.errorOptions
            );
        }


    }

    /**
     * Add reipients if they do not already exist
     * @param toRecipients 
     * @param ccRecipients 
     * @param email 
     * @returns 
     */
    addRecipient(toRecipients: emailAddress[], ccRecipients: emailAddress[], email: string) {
        if (toRecipients.some( i => i.emailAddress.address === email)) {
            return;
        }
        if (ccRecipients.some( i => i.emailAddress.address === email)) {
            return;
        }
        ccRecipients.push({ 'emailAddress': { address: email } });
    }

    addAppLogo() {
       return "<img width='600' height='300' src='cid:applogo' alt='Enterprise Technology Hub application logo' /><br/><br/>";
    }

    async getFilteredGroups(filterGroups: string[] = []): Promise<string[]> {
        let groups: string[] = [];

        try {
            const accessToken = await this.getToken(
                protectedResources.apiGraph.scopes
            );

            // Get a graph client instance for the given access token
            const graphClient = this.getGraphClient(accessToken);

            // Makes request to fetch groups list, which is expected to have multiple pages of data.
            //console.log(protectedResources.apiGraph.endpoint + "/me/checkMemberGroups");
            const response: PageCollection = await graphClient
                .api(
                    protectedResources.apiGraph.endpoint + "/me/checkMemberGroups"
                )
                .post({
                    groupIds: filterGroups,
                });

            groups = response.value;
            return groups;
        } catch (error) {
            console.log(error);
        }

        return groups;
    }

    //In testing, I may want to lookup all AD
    async findUsers(name: string): Promise<AzureUser[]> {
        let users: AzureUser[] = [];

        try {
            const accessToken = await this.getToken(
                protectedResources.apiGraph.scopes
            );

            // Get a graph client instance for the given access token
            const graphClient = this.getGraphClient(accessToken);
            let url = protectedResources.apiGraph.endpoint +"/users?";
             url += "$search=\"displayName:" + name + "\"";
                if ( environment.limitADLookupToSO) {
                    url += " AND \"displayName:CSCO\""; 
                }
                url += "&ConsistencyLevel=eventual"
                //url += "$filter=startswith(displayName,'" + name + "')";
                //"/users?$search=\"displayName:CSCO\"&ConsistencyLevel=eventual&$filter=startswith(displayName,'" + name + "')";

            // Makes request to fetch groups list, which is expected to have multiple pages of data.
            const response: PageCollection = await graphClient.api(url).get();

            users = response["value"];
            return users;
        } catch (error) {
            console.log(error);
            //this.alertService.error("XXX")
            throw error;
        }
    }

    async getUserGroupMembers(): Promise<AzureUser[]> {
        let users: AzureUser[] = [];

        try {
            const accessToken = await this.getToken(
                protectedResources.apiGraph.scopes
            );

            // Get a graph client instance for the given access token
            const graphClient = this.getGraphClient(accessToken);


            //protectedResources.apiGraph.endpoint +
            // "/groups/" +
            // groups.groupMember +
            //"?$select=id&$expand=members($select=id,mail,displayName,manager,userPrincipalName)";
            const url =
                protectedResources.apiGraph.endpoint +
                "/groups/" +
                groups.groupMember +
                "/members?$top=999&$select=id,mail,displayName,manager,userPrincipalName";

            // Makes request to fetch groups list, which is expected to have multiple pages of data.
            const response: PageCollection = await graphClient.api(url).get();

            users = response["value"];
            return users;
        } catch (error) {
            console.log(error);
            //this.alertService.error("XXX")
            throw error;
        }
    }

    async getAzureUser(azureId: string): Promise<AzureUser> {
        const url =
            protectedResources.apiGraph.endpoint +
            "/users/" +
            azureId +
            "?$expand=manager";
        return this.getUser(url);
    }

    /**
     * Gets the current user and stores the user in storage for application access.
     */
    async getCurrentUser() {
        //Note this promise is here because I was nesting API calls. I could now remove it.
        //const promise = new Promise<AzureUser>((resolve, reject) => {
        let currentUser: AzureUser = GlobalConstants.noLoggedInUser;
        //let currentManager: AzureUser = GlobalConstants.noLoggedInUser;
        this.getUser(
            protectedResources.apiGraph.endpoint + "/me?$expand=manager" +
            //select all these to add department which is not defaulted.
            "&$select=businessPhones,displayName,givenName,jobTitle,mail,mobilePhone,officeLocation,preferredLanguage,surname,userPrincipalName,id,department"
        ).then((azureUser: any) => {
            currentUser = azureUser;
            
            setCurrentUserInStorage(currentUser);
            if (!this.loginFlag) {
                //Once we are logged in, update the app insights accountID se we can track the user better.
                //Added two custom properties which is better than setting accountId IMO.
                this.applicationInsightsLoggingService.appInsights.addTelemetryInitializer((envelope) => {
                    envelope.tags = envelope.tags || [];
                    envelope.tags['ai.user.accountId'] = currentUser.displayName;
                    envelope.data = envelope.data || [];
                    envelope.data['userEmail'] = currentUser.mail;
                    envelope.data['userDisplayName'] = currentUser.displayName;

                });
                //this.applicationInsightsLoggingService.appInsights.setAuthenticatedUserContext(currentUser.mail||'',currentUser.displayName, true)
                this.loginFlag = true;
                //use custom event, so we can see a counter of sorts in the transactions search in app insights
                this.applicationInsightsLoggingService.logEvent("User Login");
                this.applicationInsightsLoggingService.logTrace("User Login: " + currentUser.displayName);
            }
            return currentUser;
            /*
                this.getUser(protectedResources.apiGraph.endpoint + "/me/manager")
                    .then((azureUserManager: AzureUser) => {
                        currentManager = azureUserManager;
                        setCurrentUserManagerInStorage(currentManager);
                        resolve(currentUser);
                    });
                */
        });
        //});
        //return promise;
    }

    async getUser(graphApiUrl: string): Promise<AzureUser> {
        const promise = new Promise<AzureUser>(async (resolve, reject) => {
            try {
                const user = Object.assign({}, GlobalConstants.noLoggedInUser);
                const accessToken = await this.getToken(
                    protectedResources.apiGraph.scopes
                );

                // Get a graph client instance for the given access token
                const graphClient = this.getGraphClient(accessToken);

                // Makes request to fetch groups list, which is expected to have multiple pages of data.
                const response: any = await graphClient.api(graphApiUrl).get();

                user.businessPhones = response.businessPhones;
                user.id = response.id;
                user.displayName = response.displayName;
                user.givenName = response.givenName;
                user.surname = response.surname;
                user.jobTitle = response.jobTitle;
                user.mail = response.mail;
                user.mobilePhone = response.mobilePhone;
                user.preferredLanguage = response.preferredLanguage;
                user.userPrincipalName = response.userPrincipalName;
                user.department = response.department;
                user.accessToken = accessToken;
                if (response.manager) {
                    user.manager = {
                        id: response.manager.id,
                        mail: response.manager.mail,
                        displayName: response.manager.displayName,
                        userPrincipalName: response.manager.userPrincipalName,
                    };
                }

                resolve(user);
            } catch (error) {
                console.log(error);
                //let user = GlobalConstants.noLoggedInUser;
                resolve(GlobalConstants.noLoggedInUser);
            }
        });
        return promise;
    }
}
